import React, { useState, useCallback } from 'react';
import * as fabric from 'fabric';

export function useCanvasSelection(
  fabricCanvasRef: React.RefObject<fabric.Canvas | null>
) {
  const [selectedProps, setSelectedProps] = useState<Record<string, any> | null>(null);
  const [hasSelection, setHasSelection] = useState<boolean>(false);

  // Directly inspect active object on canvas to return fresh, non-stale object properties
  const getFreshSelectedObjectProps = useCallback((): Record<string, any> | null => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;

    const activeObj = canvas.getActiveObject();
    if (!activeObj) return null;

    return {
      type: activeObj.type,
      fill: (activeObj.fill as string) || '#000000',
      stroke: (activeObj.stroke as string) || '#000000',
      strokeWidth: activeObj.strokeWidth || 0,
      fontFamily: (activeObj as any).fontFamily || 'Arial',
      fontSize: (activeObj as any).fontSize || 16,
      fontWeight: (activeObj as any).fontWeight || 'normal',
      fontStyle: (activeObj as any).fontStyle || 'normal',
      underline: !!(activeObj as any).underline,
      textAlign: (activeObj as any).textAlign || 'left',
      opacity: activeObj.opacity !== undefined ? activeObj.opacity : 1,
      angle: activeObj.angle || 0,
    };
  }, [fabricCanvasRef]);

  const updateSelectionState = useCallback((obj?: fabric.FabricObject) => {
    if (!obj) {
      setSelectedProps(null);
      setHasSelection(false);
      return;
    }

    setHasSelection(true);
    const freshProps = getFreshSelectedObjectProps();
    setSelectedProps(freshProps);
  }, [getFreshSelectedObjectProps]);

  const clearSelection = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.renderAll();
    setSelectedProps(null);
    setHasSelection(false);
  }, [fabricCanvasRef]);

  const selectAll = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.discardActiveObject();
    const objects = canvas.getObjects();
    if (objects.length === 0) return;

    const selection = new fabric.ActiveSelection(objects, { canvas });
    canvas.setActiveObject(selection);
    canvas.renderAll();
    setHasSelection(true);
  }, [fabricCanvasRef]);

  const bringToFront = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.bringObjectToFront(activeObject);
      canvas.renderAll();
    }
  }, [fabricCanvasRef]);

  const sendToBack = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.sendObjectToBack(activeObject);
      canvas.renderAll();
    }
  }, [fabricCanvasRef]);

  const bringForward = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.bringObjectForward(activeObject);
      canvas.renderAll();
    }
  }, [fabricCanvasRef]);

  const sendBackward = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      if (typeof (canvas as any).sendObjectBackwards === 'function') {
        (canvas as any).sendObjectBackwards(activeObject);
      } else if (typeof (canvas as any).sendObjectBackward === 'function') {
        (canvas as any).sendObjectBackward(activeObject);
      }
      canvas.renderAll();
    }
  }, [fabricCanvasRef]);

  return {
    selectedProps,
    hasSelection,
    updateSelectionState,
    getFreshSelectedObjectProps,
    clearSelection,
    selectAll,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
  };
}
